import { compose, map, prop, propOr } from 'ramda';

import { compact } from '../../../helpers/compact';
import { DocumentType } from '../../../types/document';
import { FILTER, MENU } from '../../../types/filters';
import { toRichTextFromProp, toTextFromProp } from '../fields/text';
import { deprecatedPageEntity } from './internal';

/**
 * @typedef  {Object} PrismicGouvernance
 * @property {string} ce_title
 * @property {array} ce_description
 * @property {Object} ce_main_contact
 * @property {array} ce_body //liste des contacts
 * @property {string} ca_title
 * @property {array} ca_description
 * @property {Object} ca_main_contact
 * @property {array} ca_body //liste des contacts
 * @property {string} cca_title
 * @property {array} cca_description
 * @property {array} body //liste des contacts
 */

/**
 * @typedef {DocumentSchema} GouvernanceSchema
 * @property {object} executive //CE_title,desc,main_contact,body
 * @property {object} administrative //CA_title,desc,main_contact,body
 * @property {object} representative //CCA_title,desc,main_contact,body
 */

const id = prop('id');

const membreId = compose(id, prop('membre'));

const getContacts = compose(map(membreId), propOr([], 'items'));

const getSubtitle = compose(prop('section_title'), prop('primary'));

const processContacts = compose(
  compact,
  map((d) =>
    compact({
      contacts: getContacts(d),
      subtitle: getSubtitle(d),
    })
  )
);

const processSection = (prefix, data) =>
  compact({
    title: prop(`${prefix}_title`, data),
    description: prop(`${prefix}_description`, data),
    mainContact: id(prop(`${prefix}_main_contact`, data)),
    members: processContacts(propOr([], `${prefix}_body`, data)),
  });

const processPrimary = (primary) =>
  compact({
    title: prop('titre', primary),
    description: prop('description', primary),
    mainContact: id(prop('main_contact', primary)),
  });

const processBody = (data) =>
  compact({
    members: [
      {
        contacts: map(membreId, data),
      },
    ],
  });

const processSliceSection = (section) =>
  compact({
    ...processPrimary(prop('primary', section)),
    ...processBody(prop('items', section)),
  });

const processSliceSections = compose(
  compact,
  map(processSliceSection),
  propOr([], 'cca_body')
);

const processCommittees = (data) =>
  compact({
    title: prop('cca_title', data),
    description: prop('cca_description', data),
    sections: processSliceSections(data),
  });

const processStatuses = (data) =>
  compact({
    title: toTextFromProp('ls_title', data),
    description: toRichTextFromProp('ls_description', data),
    link: prop('ls_link', data),
  });

/**
 * Transform the given input into objects for Gouvernance sections
 *
 * @param  {PrismicGouvernance} data
 * @return {GouvernanceSchema}
 */
const processGouvernance = (data) =>
  compact({
    uid: DocumentType.GOUVERNANCE,
    type: DocumentType.GOUVERNANCE,
    executive: processSection('ce', data),
    administrative: processSection('ca', data),
    committees: processCommittees(data),
    statuses: processStatuses(data),
    filters: {
      [FILTER.MENU]: MENU.IDENTITY,
    },
  });

/**
 * Normalizr schema representing an article
 */
export const Gouvernance = deprecatedPageEntity(DocumentType.GOUVERNANCE, {
  processStrategy: (entity) => ({
    ...processGouvernance(prop('data', entity)),
  }),
});

/**
 * Normalizr schema representing the gouvernance page
 */
export const GouvernanceSections = Gouvernance;
